import axios from 'axios';

// Admin API Configuration
// Use admin API URL from environment or default to deployed admin API
let ADMIN_API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL;

if (!ADMIN_API_BASE_URL) {
  console.warn('REACT_APP_ADMIN_API_URL not set, using default admin API URL');
  // For local Docker environment, use the admin API through Traefik
  if (window.location.hostname === 'dz-app.local.team-skynet.io') {
    console.warn('Using local Docker admin API URL');
    ADMIN_API_BASE_URL = 'https://dz-metrics-admin-api.local.team-skynet.io/v1';
  } else if (process.env.NODE_ENV === 'development') {
    console.warn('Using fallback admin API URL for development: http://localhost:3002/v1');
    ADMIN_API_BASE_URL = 'http://localhost:3002/v1';
  } else {
    // Production default
    ADMIN_API_BASE_URL = 'https://api-admin.dz-metrics.com/v1';
  }
}

// Create axios instance for admin API with default config
const adminApi = axios.create({
  baseURL: ADMIN_API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add admin auth token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle admin token refresh
adminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('admin_refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${ADMIN_API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          localStorage.setItem('admin_access_token', access_token);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return adminApi(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to admin login
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('admin_refresh_token');
        window.location.href = '/admin/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Admin Auth API
export const adminAuthAPI = {
  login: (credentials) => adminApi.post('/auth/login', credentials),
  refresh: (refreshToken) => adminApi.post('/auth/refresh', { refresh_token: refreshToken }),
  logout: () => adminApi.post('/auth/logout'),
};

// Admin User Management API
export const adminUserAPI = {
  getProfile: () => adminApi.get('/users/me'),
  getAllUsers: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.append(key, params[key]);
      }
    });
    return adminApi.get(`/admin/users?${queryParams}`);
  },
  getUser: (userId) => adminApi.get(`/admin/users/${userId}`),
  updateUser: (userId, userData) => adminApi.put(`/admin/users/${userId}`, userData),
  deleteUser: (userId) => adminApi.delete(`/admin/users/${userId}`),
  suspendUser: (userId) => adminApi.post(`/admin/users/${userId}/suspend`),
  activateUser: (userId) => adminApi.post(`/admin/users/${userId}/activate`),
  resetUserPassword: (userId) => adminApi.post(`/admin/users/${userId}/reset-password`),
};

// Admin System Stats API
export const adminStatsAPI = {
  getSystemOverview: () => adminApi.get('/admin/stats/overview'),
  getUserStats: (timeRange = '30d') => adminApi.get(`/admin/stats/users?time_range=${timeRange}`),
  getAPIStats: (timeRange = '24h') => adminApi.get(`/admin/stats/api?time_range=${timeRange}`),
  getPlatformStats: () => adminApi.get('/admin/stats/platforms'),
  getBillingStats: (timeRange = '30d') => adminApi.get(`/admin/stats/billing?time_range=${timeRange}`),
};

// Admin Revenue API
export const adminRevenueAPI = {
  getDashboard: (params = {}) => {
    // Mock comprehensive revenue dashboard data
    return Promise.resolve({
      data: {
        metrics: {
          mrr: 12450,
          arr: 149400,
          churnRate: 2.3,
          ltv: 1250,
          arpu: 45,
          conversionRate: 8.5,
          paymentFailures: 12
        },
        revenueHistory: [
          { month: 'Jan', mrr: 8500, arr: 102000 },
          { month: 'Feb', mrr: 9200, arr: 110400 },
          { month: 'Mar', mrr: 9800, arr: 117600 },
          { month: 'Apr', mrr: 10400, arr: 124800 },
          { month: 'May', mrr: 11000, arr: 132000 },
          { month: 'Jun', mrr: 11600, arr: 139200 },
          { month: 'Jul', mrr: 12100, arr: 145200 },
          { month: 'Aug', mrr: 12300, arr: 147600 },
          { month: 'Sep', mrr: 12450, arr: 149400 },
          { month: 'Oct', mrr: 12800, arr: 153600 },
          { month: 'Nov', mrr: 13200, arr: 158400 },
          { month: 'Dec', mrr: 13600, arr: 163200 }
        ],
        subscriptionDistribution: [
          { name: 'Free', value: 45, color: '#3B82F6' },
          { name: 'Standard', value: 35, color: '#10B981' },
          { name: 'Premium', value: 20, color: '#F59E0B' }
        ],
        paymentSuccess: [
          { name: 'Successful', value: 94.5, color: '#10B981' },
          { name: 'Failed', value: 5.5, color: '#EF4444' }
        ],
        customerGrowth: [
          { month: 'Jan', newCustomers: 18, churnedCustomers: 3, netGrowth: 15 },
          { month: 'Feb', newCustomers: 22, churnedCustomers: 5, netGrowth: 17 },
          { month: 'Mar', newCustomers: 28, churnedCustomers: 4, netGrowth: 24 },
          { month: 'Apr', newCustomers: 32, churnedCustomers: 7, netGrowth: 25 },
          { month: 'May', newCustomers: 35, churnedCustomers: 6, netGrowth: 29 },
          { month: 'Jun', newCustomers: 29, churnedCustomers: 8, netGrowth: 21 },
          { month: 'Jul', newCustomers: 31, churnedCustomers: 5, netGrowth: 26 },
          { month: 'Aug', newCustomers: 27, churnedCustomers: 9, netGrowth: 18 },
          { month: 'Sep', newCustomers: 33, churnedCustomers: 4, netGrowth: 29 },
          { month: 'Oct', newCustomers: 38, churnedCustomers: 6, netGrowth: 32 },
          { month: 'Nov', newCustomers: 25, churnedCustomers: 11, netGrowth: 14 },
          { month: 'Dec', newCustomers: 42, churnedCustomers: 7, netGrowth: 35 }
        ],
        customers: [
          { id: 1, name: 'Acme Corp', email: 'admin@acme.com', tier: 'Premium', accounts: 25, mrr: 499, status: 'Active' },
          { id: 2, name: 'TechStart Inc', email: 'contact@techstart.io', tier: 'Standard', accounts: 10, mrr: 99, status: 'Active' },
          { id: 3, name: 'Design Studio', email: 'hello@designstudio.com', tier: 'Premium', accounts: 15, mrr: 299, status: 'Active' },
          { id: 4, name: 'Marketing Inc', email: 'team@marketing.com', tier: 'Standard', accounts: 8, mrr: 99, status: 'Trial' },
          { id: 5, name: 'Small Business', email: 'owner@smallbiz.com', tier: 'Free', accounts: 3, mrr: 0, status: 'Active' },
          { id: 6, name: 'Enterprise Co', email: 'admin@enterprise.com', tier: 'Premium', accounts: 50, mrr: 999, status: 'Active' },
          { id: 7, name: 'Startup XYZ', email: 'founder@startupxyz.com', tier: 'Standard', accounts: 12, mrr: 149, status: 'Active' },
          { id: 8, name: 'Agency Plus', email: 'contact@agencyplus.com', tier: 'Premium', accounts: 30, mrr: 599, status: 'Churned' },
          { id: 9, name: 'Creative Hub', email: 'info@creativehub.com', tier: 'Premium', accounts: 18, mrr: 399, status: 'Active' },
          { id: 10, name: 'Digital Agency', email: 'hello@digitalagency.com', tier: 'Standard', accounts: 14, mrr: 199, status: 'Active' }
        ],
        monthlyReport: {
          newSubscriptions: 45,
          upgrades: 12,
          downgrades: 3,
          cancellations: 8,
          netMrrChange: 2340
        },
        quarterlyReport: {
          growthRate: 15.2,
          avgChurnRate: 2.8,
          revenueForecast: 185000,
          cac: 125,
          projectedArr: 178800
        }
      }
    });
  },
  getCustomers: (params = {}) => adminApi.get('/admin/revenue/customers', { params }),
  getReports: (params = {}) => adminApi.get('/admin/revenue/reports', { params }),
};

// Admin Support/Tickets API (mock for now - would integrate with support system)
export const adminSupportAPI = {
  getTickets: (params = {}) => {
    // Mock implementation - replace with actual support system integration
    return Promise.resolve({
      data: {
        tickets: [
          {
            id: 'TKT-001',
            subject: 'Unable to connect Instagram account',
            user: 'Sarah Johnson',
            userId: 'usr_001',
            priority: 'high',
            status: 'open',
            created: '2024-03-20T09:30:00Z',
            category: 'Platform Connection',
            lastUpdate: '2024-03-20T10:15:00Z',
            description: 'User is experiencing authentication errors when trying to connect their Instagram business account. Error code: IG_AUTH_403'
          },
          {
            id: 'TKT-002',
            subject: 'API rate limit questions',
            user: 'Mike Chen',
            userId: 'usr_002',
            priority: 'medium',
            status: 'in-progress',
            created: '2024-03-19T14:15:00Z',
            category: 'API',
            lastUpdate: '2024-03-20T08:30:00Z',
            description: 'User asking about API rate limits and how to optimize their usage'
          }
        ],
        total: 2
      }
    });
  },
  getTicket: (ticketId) => adminApi.get(`/admin/support/tickets/${ticketId}`),
  updateTicket: (ticketId, updates) => adminApi.put(`/admin/support/tickets/${ticketId}`, updates),
  respondToTicket: (ticketId, response) => adminApi.post(`/admin/support/tickets/${ticketId}/respond`, response),
};

// Admin Platform Monitoring API
export const adminPlatformAPI = {
  getPlatformStatus: () => {
    // Mock implementation - would integrate with actual monitoring
    return Promise.resolve({
      data: {
        platforms: [
          { name: 'Facebook', status: 'operational', latency: '124ms', uptime: '99.99%', lastCheck: '2024-03-20T10:18:00Z' },
          { name: 'Instagram', status: 'operational', latency: '142ms', uptime: '99.98%', lastCheck: '2024-03-20T10:18:00Z' },
          { name: 'Twitter', status: 'degraded', latency: '342ms', uptime: '98.45%', lastCheck: '2024-03-20T10:19:00Z' },
          { name: 'LinkedIn', status: 'operational', latency: '98ms', uptime: '99.99%', lastCheck: '2024-03-20T10:17:00Z' },
          { name: 'YouTube', status: 'operational', latency: '156ms', uptime: '99.97%', lastCheck: '2024-03-20T10:18:00Z' },
          { name: 'TikTok', status: 'operational', latency: '234ms', uptime: '99.85%', lastCheck: '2024-03-20T10:19:00Z' },
        ]
      }
    });
  },
  getPlatformIncidents: () => {
    return Promise.resolve({
      data: {
        incidents: [
          {
            id: 'INC-001',
            platform: 'Twitter',
            title: 'API Rate Limiting',
            description: 'Increased latency and failed requests',
            status: 'investigating',
            severity: 'medium',
            started: '2024-03-20T08:45:00Z'
          }
        ]
      }
    });
  },
};

// Admin API Monitoring
export const adminAPIMonitoringAPI = {
  getEndpointStats: (timeRange = '24h') => {
    return Promise.resolve({
      data: {
        endpoints: [
          { endpoint: '/api/v1/metrics/overview', requests: 458000, avgResponse: 98, errorRate: 0.02, status: 'healthy' },
          { endpoint: '/api/v1/platforms/connect', requests: 234000, avgResponse: 156, errorRate: 0.08, status: 'healthy' },
          { endpoint: '/api/v1/auth/login', requests: 189000, avgResponse: 67, errorRate: 0.01, status: 'healthy' },
          { endpoint: '/api/v1/campaigns', requests: 167000, avgResponse: 234, errorRate: 0.15, status: 'warning' },
          { endpoint: '/api/v1/reports/generate', requests: 89000, avgResponse: 1200, errorRate: 0.45, status: 'warning' }
        ],
        summary: {
          totalRequests: 2400000,
          avgResponseTime: 142,
          errorRate: 0.12,
          activeEndpoints: 42
        }
      }
    });
  },
  getRateLimitStats: () => {
    return Promise.resolve({
      data: {
        rateLimited: 12,
        warningThreshold: 34,
        normalUsage: 8877
      }
    });
  },
};

// Admin Social Connections API
// Note: Base URL already includes /v1, so paths here should NOT include /v1 prefix
export const adminSocialAPI = {
  getAllConnections: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.append(key, params[key]);
      }
    });
    return adminApi.get(`/admin/social/connections?${queryParams}`);
  },
  getPlatformHealth: () => adminApi.get('/admin/social/health'),
  forceTokenRefresh: (connectionId) => adminApi.post(`/admin/social/refresh/${connectionId}`),
  getPlatformMetrics: (timeRange = '30d') => adminApi.get(`/admin/social/metrics?time_range=${timeRange}`),
  togglePlatformStatus: (platform, enabled, reason = '') =>
    adminApi.post(`/admin/social/platform/${platform}/toggle`, { enabled, reason }),
  exportConnectionsData: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.append(key, params[key]);
      }
    });
    return adminApi.get(`/admin/social/export?${queryParams}`);
  },
};

// Admin Logs API
export const adminLogsAPI = {
  getSystemLogs: (params = {}) => adminApi.get('/admin/logs/system', { params }),
  getAPILogs: (params = {}) => adminApi.get('/admin/logs/api', { params }),
  getUserActivityLogs: (userId, params = {}) => adminApi.get(`/admin/logs/users/${userId}/activity`, { params }),
};

// Utility functions for admin token management
export const setAdminAuthTokens = (accessToken, refreshToken) => {
  localStorage.setItem('admin_access_token', accessToken);
  localStorage.setItem('admin_refresh_token', refreshToken);
};

export const clearAdminAuthTokens = () => {
  localStorage.removeItem('admin_access_token');
  localStorage.removeItem('admin_refresh_token');
};

export const isAdminAuthenticated = () => {
  return !!localStorage.getItem('admin_access_token');
};

export default adminApi;
